function safeEncode(value) {
  return encodeURIComponent(String(value || '').trim())
}

class ViewTracker {
  constructor(api = null) {
    this.api = api
    this.deviceId = this.getOrCreateDeviceId()
    this.trackedViews = new Set()
  }

  setApi(api) {
    this.api = api || null
  }

  getStorage() {
    try {
      return window.localStorage
    } catch (error) {
      return null
    }
  }

  getOrCreateDeviceId() {
    const storage = this.getStorage()
    const storedDeviceId = storage?.getItem('device_id')

    if (storedDeviceId) {
      return storedDeviceId
    }

    const deviceId = this.generateDeviceFingerprint()
    storage?.setItem('device_id', deviceId)
    return deviceId
  }

  generateDeviceFingerprint() {
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')

    const fingerprint = {
      userAgent: navigator.userAgent,
      language: navigator.language,
      languages: navigator.languages?.join(',') || '',
      platform: navigator.platform,
      cookieEnabled: navigator.cookieEnabled,
      doNotTrack: navigator.doNotTrack,
      screen: {
        width: screen.width,
        height: screen.height,
        colorDepth: screen.colorDepth,
        pixelDepth: screen.pixelDepth
      },
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      timezoneOffset: new Date().getTimezoneOffset(),
      canvas: this.getCanvasFingerprint(ctx, canvas)
    }

    return this.hashString(JSON.stringify(fingerprint))
  }

  getCanvasFingerprint(ctx, canvas) {
    try {
      if (!ctx) {
        return 'canvas-unavailable'
      }

      canvas.width = 200
      canvas.height = 50
      ctx.textBaseline = 'top'
      ctx.font = '14px Arial'
      ctx.fillStyle = '#f60'
      ctx.fillRect(100, 1, 62, 20)
      ctx.fillStyle = '#069'
      ctx.fillText('EWERT Cards', 2, 15)
      ctx.fillStyle = 'rgba(102, 204, 0, 0.7)'
      ctx.fillText('EWERT Cards', 4, 17)

      return canvas.toDataURL().substring(50, 100)
    } catch (error) {
      return 'canvas-error'
    }
  }

  hashString(str) {
    let hash = 0

    for (let i = 0; i < str.length; i += 1) {
      const char = str.charCodeAt(i)
      hash = ((hash << 5) - hash) + char
      hash &= hash
    }

    return `device_${Math.abs(hash)}_${Date.now()}`
  }

  collectDeviceInfo() {
    return {
      userAgent: navigator.userAgent,
      language: navigator.language,
      platform: navigator.platform,
      cookieEnabled: navigator.cookieEnabled,
      onLine: navigator.onLine,
      screen: {
        width: screen.width,
        height: screen.height,
        availWidth: screen.availWidth,
        availHeight: screen.availHeight,
        colorDepth: screen.colorDepth,
        pixelDepth: screen.pixelDepth
      },
      window: {
        innerWidth: window.innerWidth,
        innerHeight: window.innerHeight,
        outerWidth: window.outerWidth,
        outerHeight: window.outerHeight,
        devicePixelRatio: window.devicePixelRatio
      },
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      timezoneOffset: new Date().getTimezoneOffset(),
      location: this.getLocationInfo(),
      connection: this.getConnectionInfo(),
      memory: navigator.deviceMemory || null,
      hardwareConcurrency: navigator.hardwareConcurrency || null
    }
  }

  getLocationInfo() {
    return {
      available: 'geolocation' in navigator
    }
  }

  getConnectionInfo() {
    if (!('connection' in navigator)) {
      return null
    }

    const connection = navigator.connection

    return {
      effectiveType: connection.effectiveType,
      downlink: connection.downlink,
      rtt: connection.rtt,
      saveData: connection.saveData
    }
  }

  async trackCardView(cardId) {
    const normalizedCardId = String(cardId || '').trim()

    if (!normalizedCardId) {
      return null
    }

    const viewKey = `${normalizedCardId}_${this.deviceId}`
    if (this.trackedViews.has(viewKey)) {
      console.log('[VIEW] Already tracked in this session:', viewKey)
      return null
    }

    if (!this.api) {
      console.error('[VIEW] API instance is not configured')
      return null
    }

    try {
      const result = await this.api.post(`api/cards/public/${safeEncode(normalizedCardId)}/view`, {
        deviceInfo: this.collectDeviceInfo(),
        referrer: document.referrer,
        timestamp: Date.now()
      }, {
        headers: {
          'Content-Type': 'application/json',
          'X-Device-ID': this.deviceId,
          'X-Screen-Resolution': `${screen.width}x${screen.height}`,
          'X-Timezone': Intl.DateTimeFormat().resolvedOptions().timeZone
        }
      })

      if (!result || result.error) {
        console.error('[VIEW] Failed to track view:', result?.error || 'Unknown API error')
        return null
      }
      this.trackedViews.add(viewKey)

      console.log('[VIEW] View tracked successfully:', {
        cardId: normalizedCardId,
        viewId: result.viewId,
        isFirstView: result.isFirstView,
        deviceId: this.deviceId
      })

      window.dispatchEvent(new CustomEvent('cardViewTracked', {
        detail: {
          cardId: normalizedCardId,
          viewId: result.viewId,
          isFirstView: result.isFirstView
        }
      }))

      return result
    } catch (error) {
      console.error('[VIEW] Error tracking view:', error)
      return null
    }
  }

  async getCardStats(cardId, timeRange = 'all') {
    const normalizedCardId = String(cardId || '').trim()

    if (!normalizedCardId) {
      return null
    }

    if (!this.api) {
      console.error('[VIEW] API instance is not configured')
      return null
    }

    try {
      const response = await this.api.get(`api/cards/${safeEncode(normalizedCardId)}/stats`, {
        timeRange
      })

      if (!response || response.error) {
        console.error('[VIEW] Failed to get stats:', response?.error || 'Unknown API error')
        return null
      }

      return response
    } catch (error) {
      console.error('[VIEW] Error getting stats:', error)
      return null
    }
  }
}

const viewTracker = new ViewTracker()

if (typeof window !== 'undefined') {
  window.viewTracker = viewTracker
}

export { ViewTracker, viewTracker }
